import React, { useState, useEffect } from "react";
import { FolderCheck, Layers, ChevronDown, ChevronsUpDown } from "lucide-react";

/**
 * Shared Accordion/Flow Component for PTW Checklist Sections
 *
 * Supports rendering all checklist sections in one continuous flow with collapsible
 * section headers, quick section navigation chips, and Expand/Collapse All controls.
 *
 * @param {Object} props
 * @param {Array} props.sectionGroups - Output from buildChecklistSections({ sections, questions })
 * @param {string} [props.activeSectionId] - Optional controlled active section ID (for tabs mode)
 * @param {Function} [props.onSectionChange] - Optional callback when section changes
 * @param {Function} [props.getBadgeInfo] - Optional helper (sectionGroup) => { label, colorCls }
 * @param {string} [props.layout="accordion"] - "accordion" (one continuous flow with collapsible sections) or "tabs"
 * @param {Function} props.children - Render prop receiving (currentGroup, globalStartIndex)
 */
const PermitChecklistSectionTabs = ({
  sectionGroups = [],
  activeSectionId: controlledActiveId,
  onSectionChange,
  getBadgeInfo,
  layout = "accordion",
  children,
}) => {
  // Tabs-mode active tab ID
  const [internalActiveId, setInternalActiveId] = useState(
    sectionGroups[0]?.id || null
  );

  // Accordion-mode collapsed states (default all expanded = false)
  const [collapsedSections, setCollapsedSections] = useState({});

  // Sync internal state if sectionGroups changes or controlled prop passed
  useEffect(() => {
    if (controlledActiveId !== undefined) {
      setInternalActiveId(controlledActiveId);
    } else if (
      sectionGroups.length > 0 &&
      !sectionGroups.some((g) => String(g.id) === String(internalActiveId))
    ) {
      setInternalActiveId(sectionGroups[0].id);
    }
  }, [controlledActiveId, sectionGroups, internalActiveId]);

  if (!Array.isArray(sectionGroups) || sectionGroups.length === 0) {
    return null;
  }

  const hasMultipleSections = sectionGroups.length > 1;

  // -------------------------------------------------------------
  // ACCORDION (ONE CONTINUOUS FLOW) MODE - DEFAULT
  // -------------------------------------------------------------
  if (layout !== "tabs") {
    const areAllCollapsed =
      hasMultipleSections &&
      sectionGroups.every((g) => Boolean(collapsedSections[g.id]));

    const toggleSection = (groupId) => {
      setCollapsedSections((prev) => ({
        ...prev,
        [groupId]: !prev[groupId],
      }));
    };

    const toggleAll = () => {
      if (areAllCollapsed) {
        setCollapsedSections({});
      } else {
        const next = {};
        sectionGroups.forEach((g) => {
          next[g.id] = true;
        });
        setCollapsedSections(next);
      }
    };

    const handleJumpToSection = (groupId) => {
      // Ensure section is expanded
      setCollapsedSections((prev) => ({ ...prev, [groupId]: false }));
      setTimeout(() => {
        const el = document.getElementById(`ptw-section-${groupId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 50);
    };

    return (
      <div className="w-full">
        {hasMultipleSections && (
          <div className="mb-5 flex flex-wrap items-center justify-between gap-2.5 rounded-xl border border-slate-200/90 bg-slate-50/80 p-3 shadow-2xs">
            {/* Left: Section Navigator Chips */}
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <div className="mr-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600">
                <Layers className="h-4 w-4 text-orange-500" />
                <span>Sections ({sectionGroups.length}):</span>
              </div>

              {sectionGroups.map((group) => {
                const isCollapsed = Boolean(collapsedSections[group.id]);
                const qCount = group.questions?.length || 0;
                const badgeExtra = getBadgeInfo ? getBadgeInfo(group) : null;

                return (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => handleJumpToSection(group.id)}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition-all cursor-pointer ${
                      isCollapsed
                        ? "border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        : "border-orange-300 bg-orange-50 text-orange-900 shadow-2xs hover:bg-orange-100"
                    }`}
                    title={`Jump to ${group.title}`}
                  >
                    <span className="truncate max-w-[150px] sm:max-w-[220px]">
                      {group.title}
                    </span>
                    <span
                      className={`inline-flex h-4 min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold ${
                        badgeExtra?.colorCls
                          ? badgeExtra.colorCls
                          : "bg-slate-200 text-slate-700"
                      }`}
                    >
                      {badgeExtra?.label || qCount}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Right: Expand / Collapse All Toggle */}
            <button
              type="button"
              onClick={toggleAll}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 hover:text-slate-900 transition-all cursor-pointer shrink-0"
            >
              <ChevronsUpDown className="h-3.5 w-3.5 text-slate-500" />
              <span>{areAllCollapsed ? "Expand All" : "Collapse All"}</span>
            </button>
          </div>
        )}

        {/* Continuous Flow of Sections */}
        <div className="space-y-6">
          {sectionGroups.map((group, groupIdx) => {
            const isCollapsed = Boolean(collapsedSections[group.id]);
            const badgeExtra = getBadgeInfo ? getBadgeInfo(group) : null;

            return (
              <div
                key={group.id}
                id={`ptw-section-${group.id}`}
                className={
                  hasMultipleSections
                    ? "rounded-xl border border-slate-200/90 bg-white shadow-2xs overflow-hidden transition-all duration-200"
                    : ""
                }
              >
                {/* Collapsible Section Header (shown only when multiple sections exist) */}
                {hasMultipleSections && (
                  <div
                    onClick={() => toggleSection(group.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleSection(group.id);
                      }
                    }}
                    className="flex items-center justify-between gap-3 p-3.5 sm:p-4 bg-gradient-to-r from-orange-50/70 via-amber-50/30 to-slate-50 border-b border-orange-100/80 cursor-pointer select-none hover:bg-orange-50/90 transition-colors"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-orange-500/10 text-orange-600 font-bold text-xs border border-orange-200">
                        <FolderCheck className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-slate-800">
                            {group.title}
                          </span>
                          {badgeExtra && (
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold ${
                                badgeExtra.colorCls || "bg-amber-500 text-white"
                              }`}
                            >
                              {badgeExtra.label}
                            </span>
                          )}
                        </div>
                        {group.description && (
                          <p className="text-xs text-slate-500 truncate mt-0.5">
                            {group.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 shadow-2xs hover:bg-slate-50 transition-colors">
                        <span className="hidden sm:inline text-[11px]">
                          {isCollapsed ? "Expand" : "Collapse"}
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 text-slate-500 transition-transform duration-200 ${
                            isCollapsed ? "-rotate-90" : "rotate-0"
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Section Content */}
                {!isCollapsed && (
                  <div className={hasMultipleSections ? "p-4 sm:p-5 bg-slate-50/30" : ""}>
                    {typeof children === "function"
                      ? children(group, groupIdx)
                      : children}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // TAB SWITCHING MODE (BACKWARD COMPATIBILITY)
  // -------------------------------------------------------------
  const currentActiveId =
    controlledActiveId !== undefined ? controlledActiveId : internalActiveId;

  const currentGroup =
    sectionGroups.find((g) => String(g.id) === String(currentActiveId)) ||
    sectionGroups[0];

  const handleTabClick = (groupId) => {
    if (controlledActiveId === undefined) {
      setInternalActiveId(groupId);
    }
    if (typeof onSectionChange === "function") {
      onSectionChange(groupId);
    }
  };

  return (
    <div className="w-full">
      {hasMultipleSections && (
        <div className="mb-6">
          {/* Header Bar with Tabs */}
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-3">
            <div className="mr-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
              <Layers className="h-4 w-4 text-orange-500" />
              <span>Checklist Sections:</span>
            </div>

            <div className="flex flex-wrap gap-2 overflow-x-auto py-1">
              {sectionGroups.map((group) => {
                const isActive = String(group.id) === String(currentActiveId);
                const qCount = group.questions?.length || 0;
                const badgeExtra = getBadgeInfo ? getBadgeInfo(group) : null;

                return (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => handleTabClick(group.id)}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold transition-all cursor-pointer ${
                      isActive
                        ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md ring-2 ring-orange-200"
                        : "border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  >
                    <span>{group.title}</span>
                    <span
                      className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-bold ${
                        badgeExtra?.colorCls
                          ? badgeExtra.colorCls
                          : isActive
                          ? "bg-white/20 text-white"
                          : "bg-slate-200 text-slate-700"
                      }`}
                    >
                      {badgeExtra?.label || qCount}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Section Header Info */}
          {currentGroup && (
            <div className="mb-4 rounded-lg bg-orange-50/60 p-3 text-xs text-slate-700 border border-orange-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FolderCheck className="h-4 w-4 text-orange-600 shrink-0" />
                <span className="font-bold text-slate-900">
                  {currentGroup.title}
                </span>
                {currentGroup.description && (
                  <span className="text-slate-500">
                    — {currentGroup.description}
                  </span>
                )}
              </div>
              <span className="font-semibold text-orange-700 shrink-0">
                {currentGroup.questions.length}{" "}
                {currentGroup.questions.length === 1 ? "Question" : "Questions"}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Render Questions via Children Render Prop */}
      {typeof children === "function" ? children(currentGroup, 0) : children}
    </div>
  );
};

export default PermitChecklistSectionTabs;
